<script setup lang="ts">
import { NoAuthReason } from "@/models/NoAuthReason";

const store = useSongStore();
const song = computed(() => store.song);
const { t } = useI18n();
const route = useRoute();
const songNumber = computed(() => route.params.songNumber);
const slug = computed(() => store.song?.slug || route.params.slug);
const status = computed(() => store.status);

const allProducts = computed(() => {
  const products: {
    to?: string;
    href?: string;
    show: boolean;
    icon: string;
    label: string;
    subtitle?: string;
    id?: string;
    gaLoop: boolean;
  }[] = [
    {
      id: "general-tab",
      to: `/songs/${songNumber.value}/${slug.value}`,
      show: true,
      icon: "fa-light fa-info-circle",
      label: t("SV.LIT_General"),
      gaLoop: false,
    },
  ];

  if (song.value?.products.lyrics.exists) {
    products.push({
      to: `/songs/${songNumber.value}/${slug.value}/viewlyrics`,
      show: !!song.value?.products.lyrics.exists,
      icon: "fa-kit fa-ccli-lyrics",
      label: t("S.LIT_Lyrics"),
      gaLoop: false,
    });
  }
  if (song.value?.products.chords.exists) {
    products.push({
      to: `/songs/${songNumber.value}/${slug.value}/viewchordsheet`,
      show: !!song.value?.products.chords.exists,
      icon: "fa-kit fa-ccli-chord",
      label: t("S.LIT_Chords"),
      gaLoop: false,
    });
  }
  if (song.value?.products.lead.exists) {
    products.push({
      to: `/songs/${songNumber.value}/${slug.value}/viewleadsheet`,
      show: !!song.value?.products.lead.exists,
      icon: "fa-kit fa-ccli-lead",
      label: t("S.LIT_Lead"),
      gaLoop: false,
    });
  }
  if (song.value?.products.vocal.exists) {
    products.push({
      to: `/songs/${songNumber.value}/${slug.value}/viewvocalsheet`,
      show: !!song.value?.products.vocal.exists,
      icon: "fa-kit fa-ccli-vocal",
      label: t("S.LIT_Vocal"),
      gaLoop: false,
    });
  }
  if (song.value?.products.multitracks.exists) {
    products.push({
      href:
        song.value.products.multitracks.url ||
        `https://loopcommunity.com/search?query=${song.value.ccliSongNumber}&sort=-best_selling`,
      show: !!song.value.products.multitracks.exists,
      icon: "fa-kit fa-loop-logo",
      label: "Multitracks",
      subtitle: "Loop Community®",
      id: "SongLoopCommunityMultitracksLink",
      gaLoop: true,
    });
  }

  return products;
});

const showNav = computed(() => allProducts.value.length > 1);
const showProducts = computed(() => allProducts.value.filter((p) => p.show));

function getNoAuthReasonString(authReason?: NoAuthReason | null) {
  if (authReason === undefined || authReason === null) {
    return undefined;
  }

  if (authReason === NoAuthReason.NotAuthenticated) {
    return "NotAuthenticated";
  } else if (authReason === NoAuthReason.LyricsRequireSsSubscription) {
    return "LyricsRequireSsSubscription";
  } else if (authReason === NoAuthReason.RequiresSubscriptionUpgrade) {
    return "RequiresSubscriptionUpgrade";
  }
}
</script>

<template>
  <nav
    v-if="store.placeholderNotLoaded"
    id="products-nav"
    :class="`products-nav-count-6`"
  >
    <NavProductsButton
      v-for="(b, i) of 6"
      :id="'placeholder' + b"
      ref="buttonRefs"
      :key="i"
      class="nav-item"
      placeholder
    ></NavProductsButton>
  </nav>
  <nav
    v-else-if="status === LoadStatus.Ready && showNav"
    id="products-nav"
    :class="`products-nav-count-${showProducts.length}`"
    :data-songnumber="`${songNumber}`"
    :data-content-general-not-authorized="
      getNoAuthReasonString(song?.products.general.noAuthReason)
    "
    :data-content-lyrics-not-authorized="
      getNoAuthReasonString(song?.products.lyrics.noAuthReason)
    "
    :data-content-chords-not-authorized="
      getNoAuthReasonString(song?.products.chords.noAuthReason)
    "
    :data-content-lead-not-authorized="
      getNoAuthReasonString(song?.products.lead.noAuthReason)
    "
    :data-content-vocal-not-authorized="
      getNoAuthReasonString(song?.products.vocal.noAuthReason)
    "
    :data-content-multitracks-not-authorized="
      getNoAuthReasonString(song?.products.multitracks.noAuthReason)
    "
  >
    <NavProductsButton
      v-for="(b, i) of showProducts"
      :id="b.id"
      ref="buttonRefs"
      :key="i"
      class="nav-item"
      :icon="b.icon"
      :to="b.to"
      :href="b.href"
      :ga-loop="b.gaLoop"
      :active="!$route.params.slug && b.id === 'general-tab'"
      :title="b.label"
    >
      <div class="text-start">
        <div
          v-if="b.subtitle"
          class="small"
        >
          <span v-html="updateTrademark(b.subtitle)"></span>
        </div>
        <div>{{ b.label }}</div>
      </div>
    </NavProductsButton>
  </nav>
</template>

<style scoped lang="postcss">
#products-nav {
  @apply flex items-center justify-between overflow-hidden rounded-full bg-white p-0 p-1 md:gap-10;
  &.products-nav-count-1,
  &.products-nav-count-2,
  &.products-nav-count-3 {
    @apply justify-center gap-8 md:gap-16;
  }
}
.small {
  @apply text-xs leading-4;
}
</style>
